import { z } from 'zod';
import { errorSchema } from '../envelope.js';
/**
 * Helpers shared by every per-resource endpoint contract. Lives in its own
 * module so the per-resource files do not import from `./index.js` — that
 * would create an ESM cycle (index re-exports the resource modules), and
 * post-order evaluation hits the `paginationQuerySchema` const in TDZ.
 */
/**
 * Canonical pagination query. Spec §3.2. Every list endpoint composes from
 * this; coerced from URL query strings. `search` is free-form but capped to
 * keep accidental log floods bounded.
 */
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().min(1).max(200).optional(),
});
/**
 * Canonical pagination response. Spec §3.2. Producers emit this directly —
 * the wire envelope wraps it as `{success, data: paginatedResponse<T>}`.
 * The legacy `{data, meta: {...}}` shape is no longer permitted.
 */
export function paginatedResponse(item) {
    return z.object({
        items: z.array(item),
        total: z.number().int().min(0),
        page: z.number().int().min(1),
        pageSize: z.number().int().min(1),
    });
}
/**
 * Error envelope shape (failed response). Re-exported here so endpoint
 * contracts can compose response unions without crossing into `envelope.ts`.
 */
export const errorResponseSchema = z.object({
    success: z.literal(false),
    error: errorSchema,
});
/**
 * Canonical UUID-id path parameter — `{ id: <uuid> }`. Per-resource
 * endpoint modules import this and re-export aliases (e.g.
 * `userIdParamSchema = idParamSchema`) for naming clarity at call sites
 * without drift between resources.
 */
export const idParamSchema = z.object({ id: z.string().uuid() });
/**
 * Canonical "reason" body field used by yank + reject. Spec §6.1:
 * mandatory ≥ 10 characters so single-word placeholders are rejected.
 */
export const reasonSchema = z.string().min(10, 'Reason must be at least 10 characters');
/** List query base = pagination + a comma-joined signed-field sort string. */
export const listQueryBase = paginationQuerySchema.extend({
    sort: z.string().min(1).max(200).optional(),
});
/** Split a comma-joined filter value into tokens (empty/absent → []). */
export function splitFilter(value) {
    if (!value)
        return [];
    return value.split(',').filter((v) => v.length > 0);
}
/**
 * A multi-select filter param: a comma-joined string whose every token must be
 * one of `allowed`. Stays a string on the wire (round-trips through the UI
 * mutator + flattenListParams); the service splits it with `splitFilter`.
 */
export function multiFilter(allowed) {
    const set = new Set(allowed);
    return z
        .string()
        .refine((s) => splitFilter(s).every((t) => set.has(t)), {
        message: `each value must be one of: ${allowed.join(', ')}`,
    })
        .optional();
}
/**
 * A multi-value UUID filter param: a comma-joined string whose every token
 * must be a UUID. Stays a `string` schema (not `z.array`) for the same reason
 * `multiFilter` does — it round-trips through the UI mutator +
 * `flattenListParams`, and the service splits it with `splitFilter`.
 *
 * Widening an existing `z.string().uuid()` filter to this is source-compatible:
 * the inferred type is still `string | undefined`, so no caller breaks at
 * compile time and a single-uuid value still parses.
 */
export function uuidMultiFilter() {
    return z
        .string()
        .refine((s) => splitFilter(s).every((t) => z.string().uuid().safeParse(t).success), {
        message: 'each value must be a UUID',
    })
        .optional();
}
//# sourceMappingURL=_shared.js.map